import React, { useEffect, useState } from 'react'
import { bool, string } from 'prop-types'
import queryString from 'query-string'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import {
  AsyncCreateNewPassword,
  AsyncCreateNewPasswordValidate,
  AsyncInvalidCreatePassword,
  AsyncInvalidRecoveryPassword,
  AsyncNewPassword,
  AsyncNewPasswordEmailSend,
  AsyncNewPasswordValidate,
  AsyncPasswordEmailSend,
  AsyncPasswordEnter,
  AsyncPasswordIdentify,
  AsyncPasswordlessAccessCode,
  AsyncPasswordlessIdentify,
  AsyncPasswordlessReview,
  AsyncSelectClaim,
  AsyncSignupDocument,
  AsyncSignupEmail,
  AsyncSignupIdentify,
  AsyncSuccessCreatePassword,
} from './async'
import { Redirect } from '~/components/Redirect'
import { dispatchPageViewEvent } from '~/services/analyticsEvents'
import { AccessCodeFlow } from '~components/PasswordlessAccessCodeStep/constants'
import { URLS } from '~components/RoutesNavigate'
import { getPageViewExperimentId } from '~utils/analytics'

export const Pages = ({ portalUrl, appUrl, isSelectClaimEnabled }) => {
  const [currentHref, setHref] = useState('')
  const location = useLocation()

  useEffect(() => {
    if (currentHref !== window.location.toString()) {
      dispatchPageViewEvent(window.location.toString(), {
        experimentId: getPageViewExperimentId(isSelectClaimEnabled),
      })

      setHref(window.location.toString())
    }
  }, [location, currentHref, isSelectClaimEnabled])

  useEffect(() => {
    const { origin, pathname, search } = window.location
    const searchObj = queryString.parse(search)

    const isWWW = origin === portalUrl

    if (!searchObj.referrer && isWWW) {
      window.location.assign(`${appUrl}${pathname}${search}`)
    }
  }, [])

  return (
    <Routes>
      <AsyncSuccessCreatePassword path={URLS.successCreatePassword()} />

      <AsyncInvalidCreatePassword path={URLS.invalidCreatePassword()} />

      <AsyncInvalidRecoveryPassword path={URLS.invalidRecoveryPassword()} />

      <Route path={URLS.createNewPassword()}>
        <AsyncCreateNewPassword path={URLS.createNewPassword.index()} />
        <AsyncCreateNewPasswordValidate
          path={URLS.createNewPassword.validate()}
        />
      </Route>

      <Route path={URLS.newPassword()}>
        <AsyncNewPassword path={URLS.newPassword.index()} />
        <AsyncNewPasswordValidate path={URLS.newPassword.validate()} />
      </Route>

      <AsyncPasswordEmailSend path={URLS.recovery.emailSend.path()} />

      <AsyncNewPasswordEmailSend path={URLS.createPassEmailSend()} />

      <Route path={URLS.passwordless()}>
        <AsyncPasswordlessIdentify path={URLS.passwordless.identify()} />
        <AsyncPasswordlessAccessCode
          path={URLS.passwordless.accessCode()}
          accessCodeFlow={AccessCodeFlow.PASSWORDLESS_SIGN_IN}
        />
        <AsyncPasswordlessReview path={URLS.passwordless.review()} />
        <Redirect to={URLS.passwordless.identify()} path="*" />
        <Navigate to={URLS.passwordless.identify()} replace />
      </Route>

      <Route path={URLS.password()}>
        <AsyncPasswordIdentify path={URLS.password.identify()} />
        {isSelectClaimEnabled ? (
          <>
            <AsyncSelectClaim path={URLS.password.selectClaim()} />

            <AsyncPasswordlessAccessCode
              path={URLS.password.accessCode()}
              accessCodeFlow={AccessCodeFlow.SELECT_CLAIM_SIGN_IN}
            />
          </>
        ) : null}

        <AsyncPasswordEnter path={URLS.password.enter()} />
        <Redirect to={URLS.password.identify()} path="*" />
        <Navigate to={URLS.password.identify()} replace />
      </Route>

      <Route path={URLS.signup()}>
        <AsyncSignupIdentify path={URLS.signup.identify()} />
        <AsyncSignupDocument path={URLS.signup.document()} />
        <AsyncSignupEmail path={URLS.signup.email()} />
        <AsyncPasswordlessAccessCode
          path={URLS.signup.accessCode()}
          accessCodeFlow={AccessCodeFlow.SIGN_UP}
        />
        <Redirect to={URLS.signup.identify()} path="*" />
        <Navigate to={URLS.signup.identify()} replace />
      </Route>

      <Redirect
        to={
          isSelectClaimEnabled
            ? URLS.password.identify.path()
            : URLS.passwordless.identify.path()
        }
        path="*"
      />
      <Navigate
        to={
          isSelectClaimEnabled
            ? URLS.password.identify.path()
            : URLS.passwordless.identify.path()
        }
        replace
      />
    </Routes>
  )
}

Pages.propTypes = {
  appUrl: string,
  portalUrl: string,
  isSelectClaimEnabled: bool,
}
